import "./styles.css";
import { fetchPortfolioSnapshot } from "./blockchain";
import {
  EXPLORER_BASE_URL,
  MONTHLY_TARGET,
  WALLET_ADDRESS,
} from "./config";
import {
  bigintToNumber,
  errorMessage,
  formatMonth,
  formatRate,
  formatToken,
  formatUsd,
  formatUtcDateTime,
  shortenHash,
} from "./format";
import { buildMonthlySnapshot, remainingUntilMonthEnd } from "./stats";
import { fetchAllErc20Transfers } from "./transfers";
import type {
  Erc20Transfer,
  MonthlySnapshot,
  PortfolioSnapshot,
} from "./types";

interface DashboardState {
  portfolio: PortfolioSnapshot | null;
  monthly: MonthlySnapshot | null;
  portfolioError: string | null;
  historyError: string | null;
}

const state: DashboardState = {
  portfolio: null,
  monthly: null,
  portfolioError: null,
  historyError: null,
};

let refreshGeneration = 0;
let transferController: AbortController | null = null;
let toastTimer: ReturnType<typeof setTimeout> | undefined;

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`页面缺少元素 #${id}`);
  return element as T;
}

function setText(id: string, value: string) {
  const element = byId(id);
  element.textContent = value;
  element.classList.remove("metric-loading");
}

function setSourceBusy(source: "portfolio" | "history", busy: boolean) {
  document
    .querySelectorAll<HTMLElement>(`[data-source="${source}"]`)
    .forEach((element) => element.setAttribute("aria-busy", String(busy)));
}

function setupStaticLinks() {
  byId<HTMLAnchorElement>("wallet-link").href =
    `${EXPLORER_BASE_URL}/address/${WALLET_ADDRESS}`;
  setText("wallet-short", shortenHash(WALLET_ADDRESS, 8, 5));
}

function formatOracleAge(seconds: number) {
  if (seconds < 60) return `${seconds} 秒前`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)} 分钟前`;
  return `${Math.floor(seconds / 3_600)} 小时前`;
}

function renderPortfolio() {
  const snapshot = state.portfolio;
  if (!snapshot) return;

  const avaxAmount = bigintToNumber(snapshot.avaxBalance, 18);
  const aiAvaxAmount = bigintToNumber(
    snapshot.aiAvaxBalance,
    snapshot.aiAvaxDecimals,
  );
  const avaxValue = avaxAmount * snapshot.avaxUsd;
  const aiAvaxValue = aiAvaxAmount * snapshot.aiAvaxUsd;

  setText("portfolio-total", formatUsd(avaxValue + aiAvaxValue));
  setText("avax-balance", formatToken(snapshot.avaxBalance, 18));
  setText("avax-value", formatUsd(avaxValue));
  setText("avax-price", `${formatUsd(snapshot.avaxUsd)} / 枚`);
  setText(
    "aiavax-balance",
    formatToken(snapshot.aiAvaxBalance, snapshot.aiAvaxDecimals),
  );
  setText("aiavax-value", formatUsd(aiAvaxValue));
  setText("aiavax-price", `${formatUsd(snapshot.aiAvaxUsd)} / 枚`);
  setText("aiavax-avax-rate", formatRate(snapshot.aiAvaxPerAvax));
  setText("oracle-age", formatOracleAge(snapshot.oracleAgeSeconds));
  setText("oracle-time", formatUtcDateTime(snapshot.oracleUpdatedAt));
  setText("block-number", snapshot.blockNumber.toLocaleString("en-US"));

  renderGoal();
  renderTransactions();
  renderAlerts();
  renderSyncStatus();
}

function renderGoal() {
  const monthly = state.monthly;
  if (!monthly) return;
  const current = monthly.months[0];
  if (!current) return;

  const incoming = current.incoming;
  const progress = Number((incoming * 10_000n) / MONTHLY_TARGET) / 100;
  const remaining = incoming >= MONTHLY_TARGET ? 0n : MONTHLY_TARGET - incoming;
  const countdown = remainingUntilMonthEnd();
  const status = byId("goal-status");
  status.className = `status-pill ${current.completed ? "complete" : "incomplete"}`;
  status.textContent = current.completed ? "✓ 本月已完成" : "本月待完成";

  setText("month-incoming", formatToken(incoming));
  setText("progress-percent", `${progress.toLocaleString("zh-CN", { maximumFractionDigits: 2 })}%`);
  setText("remaining-amount", `${formatToken(remaining)} aiAVAX`);
  setText("month-countdown", `${countdown.days} 天 ${countdown.hours} 小时`);
  setText(
    "goal-message",
    current.completed
      ? `目标已超额完成 ${formatToken(incoming - MONTHLY_TARGET)} aiAVAX，继续保持。`
      : "所有转入和协议铸造都会累计，出账不会冲减进度。",
  );

  const progressElement = byId<HTMLProgressElement>("goal-progress");
  progressElement.value = Math.min(100, Math.max(0, progress));
  progressElement.setAttribute("aria-valuetext", `${progress.toFixed(2)}%`);

  const monthUsd = state.portfolio
    ? formatUsd(bigintToNumber(incoming) * state.portfolio.aiAvaxUsd)
    : "价格读取中";
  setText("month-usd", monthUsd);
}

function incomingType(transfer: Erc20Transfer) {
  return /^0x0{40}$/i.test(transfer.from) ? "协议铸造" : "普通转入";
}

function makeExternalIcon() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 20 20");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M7 5h8v8M15 5 6 14");
  svg.append(path);
  return svg;
}

function makeIncomingIcon() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 20 20");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M10 3v10m0 0 4-4m-4 4L6 9M4 16h12");
  svg.append(path);
  return svg;
}

function renderTransactions() {
  const monthly = state.monthly;
  if (!monthly) return;
  const list = byId("transaction-list");
  list.replaceChildren();
  setText("transaction-count", `${monthly.currentTransfers.length} 笔`);

  if (monthly.currentTransfers.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-state";
    empty.textContent = "本月尚无 aiAVAX 入账。第一枚，从这一笔开始。";
    list.append(empty);
    return;
  }

  monthly.currentTransfers.forEach((transfer) => {
    const row = document.createElement("div");
    row.className = "transaction-row";

    const kindIcon = document.createElement("span");
    kindIcon.className = "transaction-kind";
    kindIcon.append(makeIncomingIcon());

    const meta = document.createElement("div");
    meta.className = "transaction-meta";
    const metaTitle = document.createElement("strong");
    metaTitle.textContent = incomingType(transfer);
    const metaDetail = document.createElement("small");
    metaDetail.textContent = `${shortenHash(transfer.from)} · ${formatUtcDateTime(new Date(transfer.blockTimestamp * 1_000))}`;
    meta.append(metaTitle, metaDetail);

    const value = document.createElement("div");
    value.className = "transaction-value";
    const tokenValue = document.createElement("strong");
    tokenValue.textContent = `+${formatToken(transfer.value, transfer.tokenDecimals)} aiAVAX`;
    const usdValue = document.createElement("small");
    usdValue.textContent = state.portfolio
      ? formatUsd(
          bigintToNumber(transfer.value, transfer.tokenDecimals) *
            state.portfolio.aiAvaxUsd,
        )
      : "当前价值读取中";
    value.append(tokenValue, usdValue);

    const link = document.createElement("a");
    link.className = "transaction-link";
    link.href = `${EXPLORER_BASE_URL}/tx/${transfer.txHash}`;
    link.target = "_blank";
    link.rel = "noreferrer";
    link.setAttribute("aria-label", `查看交易 ${shortenHash(transfer.txHash)}`);
    link.append(makeExternalIcon());

    row.append(kindIcon, meta, value, link);
    list.append(row);
  });
}

function renderHistory() {
  const monthly = state.monthly;
  if (!monthly) return;
  const list = byId("history-list");
  list.replaceChildren();

  monthly.months.forEach((month) => {
    const row = document.createElement("div");
    row.className = `history-row${month.isCurrent ? " current" : ""}`;

    const label = document.createElement("span");
    label.textContent = `${formatMonth(month.start)}${month.isCurrent ? " · 本月" : ""}`;
    const amount = document.createElement("span");
    amount.textContent = `${formatToken(month.incoming)} aiAVAX`;
    const result = document.createElement("span");
    result.className = `history-result ${month.completed ? "complete" : "incomplete"}`;
    result.textContent = month.completed ? "已完成" : "未完成";

    row.append(label, amount, result);
    list.append(row);
  });

  renderGoal();
  renderTransactions();
  renderSyncStatus();
}

function renderAlerts() {
  const container = byId("alerts");
  container.replaceChildren();
  const messages: Array<{ text: string; warning?: boolean }> = [];

  if (state.portfolioError) {
    messages.push({ text: `余额与价格读取失败：${state.portfolioError}。请点击“刷新数据”重试。` });
  }
  if (state.historyError) {
    messages.push({ text: `月度流水读取失败：${state.historyError}。余额与价格仍可独立更新。` });
  }
  if (state.portfolio?.oracleIsStale) {
    messages.push({
      text: `Chainlink AVAX/USD 喂价已超过 2 小时未更新，美元估值可能滞后。`,
      warning: true,
    });
  }

  messages.forEach((message) => {
    const alert = document.createElement("div");
    alert.className = `alert${message.warning ? " warning" : ""}`;
    alert.textContent = message.text;
    container.append(alert);
  });
  container.hidden = messages.length === 0;
}

function renderSyncStatus() {
  const snapshots = [state.portfolio?.capturedAt, state.monthly?.capturedAt].filter(
    (value): value is Date => value instanceof Date,
  );
  if (snapshots.length > 0) {
    const latest = new Date(Math.max(...snapshots.map((date) => date.getTime())));
    setText("last-updated", latest.toISOString().slice(11, 19) + " UTC");
  }
  const available = Number(Boolean(state.portfolio)) + Number(Boolean(state.monthly));
  const errors = Number(Boolean(state.portfolioError)) + Number(Boolean(state.historyError));
  setText(
    "sync-status",
    errors > 0 ? `${available}/2 数据源可用 · ${errors} 项异常` : `${available}/2 数据源可用`,
  );
}

function setRefreshBusy(busy: boolean) {
  const button = byId<HTMLButtonElement>("refresh-button");
  button.disabled = busy;
  button.classList.toggle("loading", busy);
  button.querySelector("span")!.textContent = busy ? "正在同步" : "刷新数据";
}

async function refreshDashboard() {
  const generation = ++refreshGeneration;
  transferController?.abort();
  transferController = new AbortController();
  setRefreshBusy(true);
  setSourceBusy("portfolio", true);
  setSourceBusy("history", true);
  state.portfolioError = null;
  state.historyError = null;
  renderAlerts();

  const portfolioTask = fetchPortfolioSnapshot()
    .then((snapshot) => {
      if (generation !== refreshGeneration) return;
      state.portfolio = snapshot;
      state.portfolioError = null;
      renderPortfolio();
    })
    .catch((error: unknown) => {
      if (generation !== refreshGeneration) return;
      state.portfolioError = errorMessage(error);
      renderAlerts();
      renderSyncStatus();
    })
    .finally(() => {
      if (generation === refreshGeneration) setSourceBusy("portfolio", false);
    });

  const historyTask = fetchAllErc20Transfers(transferController.signal)
    .then((transfers) => {
      if (generation !== refreshGeneration) return;
      state.monthly = buildMonthlySnapshot(transfers, new Date());
      state.historyError = null;
      renderHistory();
    })
    .catch((error: unknown) => {
      if (generation !== refreshGeneration) return;
      state.historyError = errorMessage(error);
      renderAlerts();
      renderSyncStatus();
    })
    .finally(() => {
      if (generation === refreshGeneration) setSourceBusy("history", false);
    });

  await Promise.allSettled([portfolioTask, historyTask]);
  if (generation === refreshGeneration) {
    setRefreshBusy(false);
    renderAlerts();
    renderSyncStatus();
  }
}

function showToast(message: string) {
  const toast = byId("toast");
  toast.textContent = message;
  toast.hidden = false;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast.hidden = true;
  }, 1_800);
}

function setupInteractions() {
  byId("refresh-button").addEventListener("click", () => void refreshDashboard());
  byId("copy-wallet").addEventListener("click", async () => {
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(WALLET_ADDRESS);
      showToast("钱包地址已复制");
    } catch {
      showToast("无法复制，请从顶部钱包链接查看完整地址");
    }
  });
}

setupStaticLinks();
setupInteractions();
void refreshDashboard();

export { refreshDashboard };
